/* eslint-disable camelcase */
import { useState, useMemo, useEffect } from 'react'
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  MailOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons'
import { Button, Modal, Alert, notification } from 'antd'
import PropTypes from 'prop-types'
import { createUseStyles, useTheme } from 'react-jss'

import api from '../../api'
import { buildUrl } from '../../api/api-client'
import SectionCard from '../Common/SectionCard'
import WelcomeLetterStatus from './WelcomeLetterStatus'

const useStyles = createUseStyles({
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  statusLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: ({ theme }) => theme.textGrayColor,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  },
  preview: {
    maxWidth: '100%',
  },
  modalZoom: {
    width: 612,
  },
  '@media (max-width: 600px)': {
    modalZoom: {
      width: '90%',
    },
  },
  previewContainer: {
    position: 'relative',
  },
  zoomButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    cursor: 'pointer',
    zIndex: 1,
  },
  notification: {
    color: 'black',
  },
})

const useStylesLetter = createUseStyles({
  letter: {
    maxWidth: '100%',
  },
  letterWrapper: {
    position: 'relative',
  },
  logo: {
    position: 'absolute',
    top: '5%',
    left: '30%',
    width: '40%',
    // Cap the overlay to the template's blank header band: without a height,
    // a square-ish logo at 40% width grows to ~40% of the page height and
    // covers the letter text (the band ends where "Dear patient," starts).
    height: '18%',
    objectFit: 'contain',
    objectPosition: 'center',
  },
})

// eslint-disable-next-line react/prop-types
function Letter({ letter, logo }) {
  const classes = useStylesLetter()
  return (
    <div className={classes.letterWrapper}>
      {logo && (
        <img
          className={classes.logo}
          src={buildUrl(logo)}
          alt="Practice logo"
        />
      )}
      <img className={classes.letter} src={letter} alt="Letter preview" />
    </div>
  )
}

function WelcomeLetter({
  patient = {
    id: '',
    letterStatus: '',
    welcomeLetterTemplate: '',
  },
  practice = {
    id: '',
    logoPath: '',
  },
  showWelcomeLetterButton = false,
  collapsible = false,
  defaultCollapsed = false,
  onCollapseChange = undefined,
}) {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isZoomed, setIsZoomed] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const { letterStatus, welcomeLetterTemplate } = patient
  const letterPreview = `/images/welcome_letters/${welcomeLetterTemplate}.jpg`
  const theme = useTheme()
  const classes = useStyles({ theme })
  const { brandSuccess, brandDanger } = theme

  useEffect(() => {
    if (!isModalVisible && isZoomed) setTimeout(() => setIsZoomed(false), 500)
  }, [isModalVisible, isZoomed])

  const maxWidth = useMemo(() => (window.innerWidth >= 650 ? 612 : '90%'), [])

  const sendWelcomeLetter = async () => {
    try {
      setIsSending(true)
      await api.patients.sendWelcomeLetter(patient.id)
      setIsSending(false)
      setIsModalVisible(false)
      notification.open({
        message: 'Welcome letter queued for sending',
        description:
          'The letter will be sent shortly and the status will be updated.',
        className: classes.notification,
        icon: <CheckCircleOutlined style={{ color: brandSuccess }} />,
      })
    } catch (e) {
      notification.open({
        message: 'Error sending the welcome letter',
        description: e.message,
        className: classes.notification,
        icon: <ExclamationCircleOutlined style={{ color: brandDanger }} />,
      })
      setIsSending(false)
    }
  }

  return (
    <SectionCard
      title="Welcome Letter"
      icon={<MailOutlined />}
      collapsible={collapsible}
      defaultCollapsed={defaultCollapsed}
      onCollapseChange={onCollapseChange}
      extra={
        <Button
          type="primary"
          size="small"
          onClick={() => setIsModalVisible(true)}
          disabled={!showWelcomeLetterButton}
        >
          Preview & send
        </Button>
      }
    >
      <div className={classes.statusRow}>
        <span className={classes.statusLabel}>Status</span>
        <WelcomeLetterStatus status={letterStatus} />
      </div>
      <Modal
        title="Preview welcome letter"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => sendWelcomeLetter()}
        okText="Send welcome letter"
        okType="primary"
        width={isZoomed ? maxWidth : 400}
        confirmLoading={isSending}
      >
        <div className={classes.previewContainer}>
          <Button
            icon={isZoomed ? <ZoomInOutlined /> : <ZoomOutOutlined />}
            className={classes.zoomButton}
            onClick={() => setIsZoomed(!isZoomed)}
          />
          <Letter letter={letterPreview} logo={practice?.logoPath} />
        </div>
        <Alert
          message="Sending a welcome letter has a charge."
          type="warning"
          showIcon
        />
      </Modal>
    </SectionCard>
  )
}

WelcomeLetter.propTypes = {
  patient: PropTypes.shape(),
  practice: PropTypes.shape(),
  showWelcomeLetterButton: PropTypes.bool,
  collapsible: PropTypes.bool,
  defaultCollapsed: PropTypes.bool,
  onCollapseChange: PropTypes.func,
}

export default WelcomeLetter
